import type { ColorSpec } from './types';

/**
 * Gray color spec — uses the `slate` Tailwind palette.
 *
 * Replaces the legacy `default` special variant. The filled tone is a
 * light slate-50 bg with slate-600 text (the historical "neutral" button
 * look). Modeled on cream because both palettes use light-bg-with-dark-text
 * filled buttons rather than the dark-bg-with-white-text pattern used by
 * blue/red/green/purple/forest.
 */
export const gray: ColorSpec = {
  filled: {
    border: 'border-slate-200 dark:border-slate-700',
    bg: 'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-200',
    bgActive: 'bg-slate-500 text-white dark:bg-slate-400 dark:text-slate-900',
    hover: 'hover:bg-slate-100 active:bg-slate-200',
    hoverDark: 'dark:hover:bg-slate-700 dark:active:bg-slate-600',
  },
  outline: {
    border: 'border-slate-400 dark:border-slate-500',
    bg: 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200',
    bgActive: 'bg-slate-600 dark:bg-slate-400 text-white dark:text-slate-900',
    hover: 'hover:bg-slate-100 hover:text-slate-800 active:bg-slate-200 active:text-slate-800',
    hoverDark:
      'dark:hover:bg-slate-800 dark:hover:text-slate-100 dark:active:bg-slate-700 dark:active:text-slate-100',
    hoverWhenActive: 'hover:bg-slate-700 dark:hover:bg-slate-500',
  },
  soft: {
    border: 'border-slate-100 dark:border-slate-800',
    bg: 'bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 font-normal',
    bgActive: 'bg-slate-600 dark:bg-slate-400 text-white dark:text-slate-900',
    hover: 'hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200 active:text-slate-950',
    hoverDark:
      'dark:hover:bg-slate-800 dark:hover:text-slate-100 dark:active:bg-slate-700 dark:active:text-slate-100',
    hoverWhenActive: 'hover:bg-slate-700 dark:hover:bg-slate-500',
  },
  textFilled: {
    text: 'text-slate-600 dark:text-slate-200',
    activeBg: 'bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-100',
    hover: 'hover:bg-slate-100 active:bg-slate-200',
    hoverDark: 'dark:hover:bg-slate-800 dark:active:bg-slate-700',
  },
  textSoft: {
    text: 'text-slate-600 dark:text-slate-200 font-normal',
    activeBg: 'bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-slate-100',
    hover: 'hover:bg-slate-100 active:bg-slate-200',
    hoverDark: 'dark:hover:bg-slate-800 dark:active:bg-slate-700',
  },
  tooltip: 'bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900',
  badge: {
    filled: 'bg-slate-600 dark:bg-slate-400 text-white dark:text-slate-900',
    soft: 'bg-slate-400 dark:bg-slate-500 text-white',
  },
  softIcon: 'text-slate-700 dark:text-slate-200',
  underline: {
    filled:
      '[&_u]:text-slate-200 dark:[&_u]:text-slate-300 [&_u]:decoration-slate-300 dark:[&_u]:decoration-slate-400',
    outline:
      '[&_u]:text-slate-800 dark:[&_u]:text-slate-300 [&_u]:decoration-slate-600 dark:[&_u]:decoration-slate-400',
    soft: '[&_u]:text-slate-800 dark:[&_u]:text-slate-300 [&_u]:decoration-slate-300 dark:[&_u]:decoration-slate-400',
  },
  input: {
    filled: {
      border: 'border-slate-200 dark:border-slate-700',
      bg: 'bg-slate-50 dark:bg-slate-800',
      focusBg: 'focus-within:bg-apricot-white-50 dark:focus-within:bg-charade-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-slate-200 dark:focus-within:outline-slate-700',
      hoverActive:
        'hover:bg-slate-100 active:bg-slate-200 dark:hover:bg-slate-700 dark:active:bg-slate-600 focus-within:hover:bg-apricot-white-50 dark:focus-within:hover:bg-charade-800',
      readOnlyBg: 'bg-stone-50 dark:bg-slate-900',
      text: 'text-slate-600 dark:text-slate-200',
    },
    outline: {
      border: 'border-slate-400 dark:border-slate-500',
      bg: 'bg-white dark:bg-slate-900',
      focusBg: 'focus-within:bg-slate-50 dark:focus-within:bg-slate-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-slate-400 dark:focus-within:outline-slate-500',
      hoverActive:
        'hover:bg-slate-100 hover:text-slate-800 active:bg-slate-200 active:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-100 dark:active:bg-slate-700 dark:active:text-slate-100',
      readOnlyBg: 'bg-slate-50 dark:bg-slate-900',
      text: 'text-slate-700 dark:text-slate-200',
    },
    soft: {
      border: 'border-slate-100 dark:border-slate-800',
      bg: 'bg-slate-50 dark:bg-slate-900',
      focusBg: 'focus-within:bg-slate-100 dark:focus-within:bg-slate-800',
      focusOutline:
        'focus-within:outline-solid focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-slate-100 dark:focus-within:outline-slate-800',
      hoverActive:
        'hover:bg-slate-100 hover:text-slate-900 active:bg-slate-200 active:text-slate-950 dark:hover:bg-slate-800 dark:hover:text-slate-100 dark:active:bg-slate-700 dark:active:text-slate-100',
      readOnlyBg: 'bg-slate-50 dark:bg-slate-900',
      text: 'text-slate-700 dark:text-slate-300',
    },
  },
  inputMuted: {
    filled: 'text-slate-600 dark:text-slate-100',
    outline: 'text-slate-400 dark:text-slate-600',
    soft: 'text-slate-400 dark:text-slate-600',
  },
  inputPlaceholder: {
    filled: 'placeholder:text-slate-400 dark:placeholder:text-slate-600',
    outline: 'placeholder:text-slate-400 dark:placeholder:text-slate-600',
    soft: 'placeholder:text-slate-400 dark:placeholder:text-slate-600',
  },
  group: {
    filled: 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800',
    outline: 'border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-900',
    soft: 'border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900',
  },
  groupDivider: {
    filled: 'bg-slate-200 dark:bg-slate-700',
    outline: 'bg-slate-300 dark:bg-slate-600',
    soft: 'bg-slate-200 dark:bg-slate-700',
  },
  segment: {
    filled: 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800',
    outline: 'border-slate-400 dark:border-slate-500 bg-white dark:bg-slate-900',
  },
  segmentDivider: {
    filled: 'bg-slate-200 dark:bg-slate-700',
    outline: 'bg-slate-300 dark:bg-slate-600',
  },
  popoverHighlight: 'bg-astronaut-600 text-white',
  popoverText: 'text-slate-600 dark:text-slate-100',
  popoverBorder: 'border-slate-200 dark:border-charade-700',
};
